import cn from 'classnames';
import { Link as GatsbyLink } from 'gatsby';
import { isString, over, startsWith } from 'lodash/fp';
import React, { forwardRef } from 'react';

import { createHref, equalBasePath } from '@skippay/core/utils/routeUtils';

import { URL_BLOG } from 'constants/routes';

const isActiveFromProps =
	({ activeClassName, className, exact }) =>
	({ href, isCurrent, location: { pathname } }) => ({
		className:
			isCurrent || (!exact && equalBasePath(href, pathname))
				? cn(className, activeClassName)
				: className,
	});

const absoluteLinkRe = /^[^/]+:/;
const pointsToFileRe = /\.[^/]+$/;
/**
 * Underlying anchor `<a/>` component
 */
const TouchableLink = forwardRef(function TouchableLink(props, ref) {
	const {
		activeClassName,
		disabled,
		exact,
		shareUserSession,
		onClick,
		onPopupBrowserClose,
		openInPopupBrowser,
		shouldClosePopupBrowser,
		state,
		staticContext,
		to,
		untrusted,
		// unused styled props, we do not want to pass down to the html element (fixes https://mallpay.atlassian.net/browse/SS-7527)
		breakout,
		textDecoration,
		variant,
		width,
		withImage,
		...other
	} = props;

	if (disabled) {
		return <span {...other} ref={ref} />;
	}

	if (isString(to)) {
		const absoluteLink = absoluteLinkRe.test(to);
		if (untrusted && !absoluteLink) {
			// eslint-disable-next-line no-console
			console.warn(
				`Attempt to render untrusted link with href "${to}". To prevent XSS, the link will be rendered but wont work. Untrusted links must start with "http://" or "https://"`
			);
			return <span {...other} ref={ref} />;
		}
		if (absoluteLink || pointsToFileRe.test(to) || other.target === `_blank`) {
			const externalLink =
				absoluteLink && (!to.startsWith(process.env.SELF_CARE_URL) || !to.startsWith(URL_BLOG));
			return (
				// eslint-disable-next-line jsx-a11y/anchor-has-content
				<a
					{...(startsWith(to, 'http') && {
						target: '_blank',
						rel: `noopener noreferrer${externalLink ? ' nofollow' : ''}`,
					})}
					{...other}
					onClick={onClick}
					href={to}
					ref={ref}
				/>
			);
		}
	}

	return (
		<GatsbyLink
			{...other}
			getProps={isActiveFromProps(props)}
			onClick={over([
				onClick,
				shouldClosePopupBrowser &&
					((e) => {
						if (!e.isDefaultPrevented()) {
							window.redirectPopupBrowser = to;
						}
					}),
			])}
			to={createHref(to)}
			ref={ref}
			state={state || to.state}
		/>
	);
});

export default TouchableLink;
